/**
 * Lazy-loads the Carousel module (and Splide with it) on first use.
 *
 * @returns {Promise<typeof import('./Carousel').default>} The Carousel class.
 */
const loadCarousel = () => import(/* webpackChunkName: "carousel" */ './Carousel').then((module) => module.default);

/**
 * Builds and mounts a carousel on the given element.
 *
 * @param {HTMLElement} element The `.js-carousel` container.
 * @returns {Promise<void>}
 */
const mountCarousel = async (element) => {
	let Carousel;

	try {
		Carousel = await loadCarousel();
	} catch (error) {
		// e.g. a stale chunk after a deploy; the slides stay as plain, unmounted markup.
		console.error('Carousel failed to load', error);
		return;
	}

	const carousel = new Carousel(element);

	if (carousel.slider) {
		carousel.slider.mount();
	}
};

/**
 * Mounts every `.js-carousel` on the page, and any lazyloaded one when it unveils.
 * Splide is only fetched once a carousel actually needs mounting.
 *
 * @returns {void}
 */
export default function initCarousels () {

	const carouselElements = document.getElementsByClassName('js-carousel');

	for (const element of carouselElements) {

		if (element.classList.contains('lazyloaded') || !element.classList.contains('lazyload')) {
			mountCarousel(element);
		}

	}

	// Registered synchronously so an unveil fired before the chunk arrives isn't missed.
	document.addEventListener(
		'lazybeforeunveil',
		(e) => {

			if (e.target.classList.contains('js-carousel')) {
				mountCarousel(e.target);
			}

		}
	);

}
